import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { CHAT_MAX_LENGTH } from '../../game/events';
import { codeOf } from '../../game/shortcuts';

/**
 * Scrollback and input for proximity chat.
 *
 * REACT STATE HERE IS CORRECT, unlike snapshots. Messages arrive occasionally
 * and drive DOM, which is exactly what React is for — the rule that keeps game
 * entities out of React is about the 20Hz tick, not about everything.
 *
 * THE KEYBOARD HANDOFF IS THE WHOLE PROBLEM. Movement keys are bound to
 * `window` and W/A/S/D call `preventDefault()`, so while this input has focus
 * the game must stop listening or typing "was" walks the character and eats the
 * letters. `onFocusChange` is that handshake; Input.js guards it a second time
 * by ignoring events aimed at a text field.
 *
 * IT SITS ON THE GAME, not under it. Stacked below the canvas it was off the
 * bottom of a laptop screen, so reading chat meant scrolling away from the
 * thing you were playing — which is the one moment you cannot afford to look
 * away. RuneScape's answer, and now this one: the view and the chat are a
 * single frame that never scrolls.
 *
 * DIMMED WHEN NOTHING IS HAPPENING, so it stops covering the world during the
 * long quiet stretches, and full the moment somebody speaks. It must never dim
 * while focused — typing into something you cannot read is worse than either
 * state.
 */

/**
 * Enter focuses chat, Escape returns to the game. The MMO reflex.
 *
 * From the shortcuts registry rather than spelled here, so the legend and the
 * behaviour cannot disagree. Constants only — none of the focus handshake below
 * changes.
 */
const OPEN_KEY = codeOf('chatOpen');
const CLOSE_KEY = codeOf('chatClose');

/** How long a message keeps chat at full opacity after arriving. */
const ATTENTION_MS = 8000;

function ChatPanel({ messages, onSend, onFocusChange }) {
  const inputRef = useRef(null);
  const logRef = useRef(null);
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [recent, setRecent] = useState(false);

  // A message just arrived: come back to full, then settle again.
  useEffect(() => {
    if (messages.length === 0) return undefined;
    setRecent(true);
    const timer = setTimeout(() => setRecent(false), ATTENTION_MS);
    return () => clearTimeout(timer);
  }, [messages]);

  // Enter from anywhere focuses the box. Bound to the document rather than the
  // canvas because the canvas is not focusable and clicks may land anywhere.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== OPEN_KEY) return;
      if (document.activeElement === inputRef.current) return;
      event.preventDefault();
      inputRef.current?.focus();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Pin to the newest message. Only when already near the bottom, so reading
  // back through scrollback is not yanked away by someone else talking.
  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    const nearBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 60;
    if (nearBottom) log.scrollTop = log.scrollHeight;
  }, [messages]);

  const submit = useCallback((event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft('');
  }, [draft, onSend]);

  const onKeyDown = (event) => {
    if (event.key === CLOSE_KEY) {
      event.currentTarget.blur();
    }
    // Stop movement keys reaching the window listener even though Input.js
    // already ignores them — two guards, because the failure is silent.
    event.stopPropagation();
  };

  const awake = focused || hovered || recent;

  return (
    /*
      `pointer-events-auto` because the wrapper above this is
      `pointer-events-none` so clicks reach the world. Without it the input
      cannot be clicked; without the wrapper's `none`, an invisible box would
      swallow every click on the game.
    */
    <div
      className={`pointer-events-auto flex w-full flex-col gap-1.5 transition-opacity duration-300 ${
        awake ? 'opacity-100' : 'opacity-45'
      }`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        ref={logRef}
        className="h-28 overflow-y-auto rounded-lg border border-gray-800/80 bg-gray-950/85 p-2 text-xs font-mono space-y-0.5 backdrop-blur-sm"
        aria-live="polite"
        aria-label="Chat messages"
      >
        {messages.length === 0 && (
          <p className="text-gray-600">
            Nobody has said anything yet. Press Enter to type — only players
            near you will hear it.
          </p>
        )}

        {messages.map((m) => (
          <p key={m.key} className={m.system ? 'text-primary/80 italic' : 'text-gray-300'}>
            {!m.system && (
              <span style={{ color: m.color }} className="font-semibold">
                {m.name}
                {': '}
              </span>
            )}
            {m.text}
          </p>
        ))}
      </div>

      <form onSubmit={submit} className="flex gap-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => { setFocused(true); onFocusChange(true); }}
          onBlur={() => { setFocused(false); onFocusChange(false); }}
          maxLength={CHAT_MAX_LENGTH}
          placeholder="Press Enter to chat, Escape to play. /help for commands."
          aria-label="Chat message"
          className="flex-1 rounded bg-gray-900 border border-gray-800 px-3 py-1.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-primary"
        />
        <button
          type="submit"
          disabled={!draft.trim()}
          className="px-4 py-1.5 rounded bg-primary text-navy text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Send
        </button>
      </form>
    </div>
  );
}

ChatPanel.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.number.isRequired,
    system: PropTypes.bool,
    name: PropTypes.string,
    color: PropTypes.string,
    text: PropTypes.string,
  })).isRequired,
  /** @param {string} text — already trimmed; the server sanitizes again. */
  onSend: PropTypes.func.isRequired,
  /** True while the input holds focus, so the game can release the keyboard. */
  onFocusChange: PropTypes.func.isRequired,
};

export default ChatPanel;
